import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useTenantData } from '../../contexts/TenantDataContext';
import {
  X,
  Home,
  MessageSquare,
  BookOpen,
  Users,
  FileText,
  LayoutTemplate,
  GraduationCap,
  Mic,
  Settings,
  Building2,
  MessageCircle,
  UsersRound,
  LogOut,
  ChevronRight,
} from 'lucide-react';
import { openCommunityGroup } from '../../constants/externalLinks';

// Mirrors the React Native drawer (`react-native/src/components/navigation/CustomDrawerContent.tsx`)
// so the mobile web menu looks identical to the native app's side drawer.
const LOGO_URL = 'https://routemize-public-web.s3.us-east-1.amazonaws.com/new-logo.png';

const MENU = {
  bg: '#0f172a',
  gradientOverlay: 'rgba(139, 92, 246, 0.03)',
  itemActiveBg: 'rgba(255, 255, 255, 0.12)',
  itemColor: 'rgba(255, 255, 255, 0.7)',
  border: 'rgba(255, 255, 255, 0.08)',
};

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenuDrawer: React.FC<MobileMenuDrawerProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { config } = useAppConfig();
  const { requestsBadgeCount, bookingsBadgeCount } = useNotifications();
  const { canManageRequests, canEditBookingForms, canEditCompanySettings, canAccessVoiceAgent } = usePermissions();
  const { available: hasVoiceAgent, loading: voiceAgentLoading } = useFeatureAccess('VoiceAgent');
  const { branding: { logoPath, hasCustomLogo, primaryColor, secondaryColor, businessName } } = useTenantData();
  const showVoiceAgent = canAccessVoiceAgent && !voiceAgentLoading && hasVoiceAgent;
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on route change
  useEffect(() => {
    onClose();
  }, [location.pathname]);

  // Handle escape key + lock body scroll while open
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const getUserInitials = () => {
    const currentUser = config?.currentUser;
    const name = currentUser?.name?.trim();
    const surName = currentUser?.surName?.trim();
    if (name && surName) return (name[0] + surName[0]).toUpperCase();
    if (name) {
      const parts = name.split(' ');
      return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
    }
    return currentUser?.email?.[0]?.toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    const currentUser = config?.currentUser;
    const name = currentUser?.name?.trim();
    const surName = currentUser?.surName?.trim();
    if (name && surName) return `${name} ${surName}`;
    if (name) return name;
    return currentUser?.email?.split('@')[0] || 'User';
  };

  const getCompanyInitials = () => {
    if (!businessName) return 'CO';
    const words = businessName.trim().split(' ');
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return businessName.slice(0, 2).toUpperCase();
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = async () => {
    onClose();
    await logout();
  };

  const handleSupport = () => {
    onClose();
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('show');
    }
  };

  const handleCommunity = () => {
    onClose();
    openCommunityGroup();
  };

  const showCompanyBranding = !!businessName && businessName !== 'Your Business';

  const navItems = [
    { to: '/dashboard', icon: Home, label: 'Dashboard', visible: true },
    { to: '/requests', icon: MessageSquare, label: 'Requests', badge: requestsBadgeCount, visible: canManageRequests },
    { to: '/bookings', icon: BookOpen, label: 'Bookings', badge: bookingsBadgeCount, visible: true },
    { to: '/contacts', icon: Users, label: 'Contacts', visible: true },
    { to: '/booking-forms', icon: FileText, label: 'Booking Forms', visible: canEditBookingForms },
    { to: '/landing-pages', icon: LayoutTemplate, label: 'Landing Pages', visible: canEditBookingForms },
    { to: '/voice-agent', icon: Mic, label: 'Voice Agent', visible: showVoiceAgent },
    { to: '/tutorials', icon: GraduationCap, label: 'Tutorials', visible: true },
  ].filter((item) => item.visible);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        style={{ animation: 'fadeIn 200ms ease-out' }}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 bottom-0 w-[300px] max-w-[88vw] shadow-2xl flex flex-col overflow-hidden"
        style={{ background: MENU.bg, animation: 'slideInFromRight 300ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Subtle violet gradient overlay (matches native) */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: MENU.gradientOverlay }} />

        {/* Logo Header */}
        <div
          className="relative flex items-center justify-between px-4 py-4"
          style={{ borderBottom: `1px solid ${MENU.border}` }}
        >
          <Link to="/dashboard" className="flex items-center" onClick={onClose}>
            <img src={LOGO_URL} alt="Routemize" className="h-9 w-[140px] object-contain object-left" />
          </Link>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-white/50 hover:text-white transition-colors"
            style={{ background: 'rgba(255, 255, 255, 0.08)' }}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="relative flex-1 overflow-y-auto pb-4">
          {/* Company Branding card */}
          {showCompanyBranding && (
            <div className="px-3 pt-3 pb-1">
              <Link
                to={canEditCompanySettings ? '/settings' : '/profile'}
                onClick={onClose}
                className="relative flex items-center p-3.5 rounded-2xl overflow-hidden"
                style={{ background: 'rgba(255, 255, 255, 0.06)', border: `1px solid ${primaryColor}30` }}
              >
                {/* Brand accent bar */}
                <span
                  className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl"
                  style={{ background: primaryColor }}
                />
                <div className="mr-3 flex-shrink-0">
                  {hasCustomLogo && logoPath ? (
                    <img src={logoPath} alt={businessName} className="w-10 h-10 rounded-xl object-cover" />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                    >
                      <span className="text-white text-15 font-bold">{getCompanyInitials()}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-15 font-semibold text-white leading-tight line-clamp-2">
                    {businessName}
                  </span>
                </div>
                <Settings size={18} className="ml-2 flex-shrink-0 text-white/50" />
              </Link>
            </div>
          )}

          {/* Main Navigation */}
          <nav className="px-3 pt-2 space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="relative flex items-center px-3.5 py-3 rounded-xl overflow-hidden transition-colors"
                  style={{ background: active ? MENU.itemActiveBg : 'transparent' }}
                >
                  {active && (
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r"
                      style={{ background: 'linear-gradient(180deg, #a78bfa, #818cf8)' }}
                    />
                  )}
                  <span className="w-6 flex items-center justify-center mr-3.5">
                    <Icon size={20} strokeWidth={active ? 2.5 : 2} color={active ? '#FFFFFF' : MENU.itemColor} />
                  </span>
                  <span
                    className="flex-1 text-sm tracking-wide"
                    style={{ color: active ? '#FFFFFF' : MENU.itemColor, fontWeight: active ? 600 : 500 }}
                  >
                    {item.label}
                  </span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      className="text-white text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ background: 'linear-gradient(135deg, #f43f5e, #ec4899)' }}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Sticky Bottom Section */}
        <div
          className="relative px-3 pt-3 pb-4 space-y-2"
          style={{ borderTop: `1px solid ${MENU.border}`, background: MENU.bg }}
        >
          {/* Account card */}
          <div
            className="flex items-center p-2.5 rounded-xl"
            style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}
          >
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #8b5cf6, #818cf8)' }}
            >
              <span className="text-white text-11 font-bold">{getUserInitials()}</span>
            </div>
            <div className="flex-1 min-w-0 ml-2.5">
              <span className="block text-9 font-medium uppercase tracking-wider text-white/45">Account</span>
              <span className="block text-13 font-semibold text-white truncate">{getUserDisplayName()}</span>
            </div>
            <button
              onClick={() => { onClose(); navigate('/profile'); }}
              className="p-1.5 rounded-lg flex-shrink-0"
              style={{ background: 'rgba(255, 255, 255, 0.06)' }}
              aria-label="Account settings"
            >
              <Settings size={18} className="text-white/50" />
            </button>
          </div>

          {/* Company Settings card */}
          {canEditCompanySettings && (
            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center p-2.5 rounded-xl"
              style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}
            >
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(99, 102, 241, 0.2)' }}
              >
                <Building2 size={12} className="text-indigo-400" />
              </div>
              <div className="flex-1 min-w-0 ml-2.5">
                <span className="block text-9 font-medium uppercase tracking-wider text-white/45">Company</span>
                <span className="block text-13 font-semibold text-white">Settings</span>
              </div>
              <ChevronRight size={14} className="text-white/40 flex-shrink-0" />
            </Link>
          )}

          {/* Community group chat on Facebook Messenger */}
          <button
            onClick={handleCommunity}
            className="w-full flex items-center p-2.5 rounded-xl"
            style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}
          >
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(59, 130, 246, 0.2)' }}
            >
              <UsersRound size={12} className="text-blue-400" />
            </div>
            <div className="flex-1 min-w-0 ml-2.5 text-left">
              <span className="block text-9 font-medium uppercase tracking-wider text-white/45">Community</span>
              <span className="block text-13 font-semibold text-white">Join the group chat</span>
            </div>
            <ChevronRight size={14} className="text-white/40 flex-shrink-0" />
          </button>

          {/* Support & Sign Out */}
          <div
            className="flex rounded-xl overflow-hidden"
            style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}
          >
            <button
              onClick={handleSupport}
              className="flex-1 flex items-center justify-center gap-1.5 py-3"
              style={{ color: '#06B6D4' }}
            >
              <MessageCircle size={18} />
              <span className="text-13 font-semibold">Support</span>
            </button>
            <span className="w-px" style={{ background: 'rgba(255, 255, 255, 0.06)' }} />
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-1.5 py-3"
              style={{ color: '#EF4444' }}
            >
              <LogOut size={18} />
              <span className="text-13 font-semibold">Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInFromRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default MobileMenuDrawer;
